import React, { useEffect, useMemo } from 'react';
import { BsLink45Deg } from 'react-icons/bs';
import { MdInfoOutline } from 'react-icons/md';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { GridItem, Icon, IconButton, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverTrigger, Text, useDisclosure } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import PaginatedTable from '../../../../../../../../../../../components/PaginatedTable';
import LoadingSpinner from '../../../../../../../../../../../components/Spinner';
import { CodeStatuses } from '../../../../../../../../../../../constants/codes';
import { getPolicyLinkedItems } from '../../../../../../../../../../../redux/modules/insured';
import { useCompanyItemTypesData } from '../../../../../../../../../../../services/company-service';
import { createHeaderCell } from '../../../../../../../../../../../utils/tableUtil';
import createItemCodesHeaderTooltip from '../../../../../../../../../../InsuredProfilePage/components/ItemTable/ItemCodesHeaderTooltip';
import CodeCell from '../../../../../../../../../../InsuredProfilePage/components/ItemTable/ItemTableContent/cells/CodeCell';
import InsuranceRequirementsCell from '../../../../../../../../../../InsuredProfilePage/components/ItemTable/ItemTableContent/cells/InsuranceRequirementsCell';

const defaultColumns = [
  {
    Header: createItemCodesHeaderTooltip(MdInfoOutline, 'Codes'),
    id: 'code',
    accessor: item => item.codes?.map(itemCode => ({
      letter: itemCode.code.letter,
      status: itemCode.status?.status || CodeStatuses.Applied
    })),
    Cell: p => <CodeCell hidePopover {...p} />,
    order: 1,
    disableSortBy: true
  },
  {
    Header: createHeaderCell('Min. Req. Coverage'),
    id: 'requirement',
    accessor: item => item.insuranceRequirement,
    Cell: InsuranceRequirementsCell,
    order: 100,
    disableSortBy: true
  }
];

const PolicyItemsPopover = ({ id, companyId, data, pageInfo, disabled }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const { data: itemTypes, isLoading: itemTypesLoading } = useCompanyItemTypesData(companyId);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const itemTypeProperties = useMemo(() => {
    if (itemTypesLoading) {
      return [];
    }

    const itemType = itemTypes.find(type => type.name === 'unit');

    return itemType?.itemTypeProperties ?? [];
  }, [itemTypes, itemTypesLoading]);

  useEffect(() => {
    if (!data && isOpen) {
      dispatch(getPolicyLinkedItems(id, params.insuredId));
    }
  }, [id, data, isOpen, dispatch, params.insuredId]);

  const handlePageChange = page => {
    dispatch(getPolicyLinkedItems(id, params.insuredId, page));
  };

  const hiddenColumns = [
    'quote_number',
    'vehicle_quote_status',
    'approval_status',
    'status',
    'delv_dte'
  ];

  const columns = useMemo(() => itemTypeProperties
    .map(p => ({
      Header: createHeaderCell(p.displayName),
      accessor: p.name,
      order: p.order,
      disableSortBy: true
    }))
    .concat(defaultColumns)
    .sort((a, b) => a.order - b.order), [itemTypeProperties]);

  const mappedData = useMemo(() => data && data.map(({
    insurable: {
      properties,
      ...rest
    }
  }) => properties.reduce((row, property) => {
    const { itemTypeProperty: { name, dataType }, value } = property;
    return {
      ...row,
      [name]: value || ''
    };
  }, { ...rest })), [data]);

  if (disabled) {
    return (
      <GridItem colSpan={1}>
        <IconButton
          isDisabled
          icon={<Icon as={BsLink45Deg} h={5} w={5} />}
          variant="textBlack"
        />
      </GridItem>
    );
  }

  return (
    <GridItem colSpan={1}>
      <Popover
        isLazy
        isOpen={isOpen}
        placement="bottom"
        trigger="hover"
        onClose={onClose}
        onOpen={onOpen}
      >
        <PopoverTrigger>
          <IconButton
            icon={<Icon as={BsLink45Deg} h={5} w={5} />}
            variant="textPrimary"
          />
        </PopoverTrigger>
        <PopoverContent>
          <PopoverArrow />
          <PopoverCloseButton />
          <PopoverBody mt={3} mx={2}>
            {mappedData && !itemTypesLoading ? (
              <>
                <Text mb={2} textAlign="left" textStyle="bodyXl">
                  {`Linked Items (${pageInfo?.total})`}
                </Text>
                <PaginatedTable
                  columns={columns}
                  data={mappedData}
                  handlePageChange={page => handlePageChange(page)}
                  hiddenColumns={hiddenColumns}
                  maxHeight="400px"
                  pageInfo={pageInfo}
                />
              </>
            ) : (
              <GridItem colSpan={1} minW="200px">
                <LoadingSpinner isInline minHeight="200px" />
              </GridItem>
            )}
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </GridItem>
  );
};

PolicyItemsPopover.propTypes = {
  companyId: PropTypes.number.isRequired,
  id: PropTypes.number,
  data: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number })),
  pageInfo: PropTypes.shape({ total: PropTypes.number }),
  disabled: PropTypes.bool
};

export default PolicyItemsPopover;

// TODO: this is the react query without the redux paginated request.
// I couldn't figure out how to hit the correct accessor within the react query infinite data set

// import React, { useMemo } from 'react';
// import { BsLink45Deg } from 'react-icons/bs';
// import { MdInfoOutline } from 'react-icons/md';
// import { useDispatch } from 'react-redux';
// import { GridItem, Icon, IconButton, Popover, PopoverArrow, PopoverBody, PopoverCloseButton, PopoverContent, PopoverTrigger, Text, useDisclosure } from '@chakra-ui/react';
// import PropTypes from 'prop-types';
//
// import PaginatedTable from '../../../../../../../components/PaginatedTable';
// import ReactQueryInfiniteTable from '../../../../../../../components/ReactQueryInfiniteTable';
// import LoadingSpinner from '../../../../../../../components/Spinner';
// import { CodeStatuses } from '../../../../../../../constants/codes';
// import { useCompanyItemTypesData } from '../../../../../../../services/company-service';
// import policyKeys from '../../../../../../../services/models/policy/keys';
// import { usePolicyLinkedItemsData } from '../../../../../../../services/policy-service';
// import { getInfiniteListData } from '../../../../../../../services/util';
// import { queryClient } from '../../../../../../../utils/queryClientUtil';
// import { createHeaderCell } from '../../../../../../../utils/tableUtil';
// import CodeCell from '../../../../Items/components/ItemTable/cells/CodeCell';
// import InsuranceRequirementsCell from '../../../../Items/components/ItemTable/cells/InsuranceRequirementsCell';
// import PropertyCell from '../../../../Items/components/ItemTable/cells/PropertyCell';
// import UnitNumberCell from '../../../../Items/components/ItemTable/cells/UnitNumberCell';
// import ItemCodesHeaderTooltip from '../../../../Items/components/ItemTable/components/ItemCodesHeaderTooltip';
// import { buildItemColumns } from '../../../../Items/components/ItemTable/util';
//
// const getDefaultColumns = (companyId, insuredId) => [
//   {
//     Header: () => (<ItemCodesHeaderTooltip companyId={companyId} insuredId={insuredId} />),
//     id: 'code',
//     accessor: item => item.insurable.codes?.map(itemCode => ({
//       letter: itemCode.code.letter,
//       status: itemCode.status?.status || CodeStatuses.Applied
//     })),
//     Cell: p => <CodeCell hidePopover {...p} />,
//     order: 1,
//     disableSortBy: true
//   },
//   {
//     Header: createHeaderCell('Min. Req. Coverage'),
//     id: 'requirement',
//     accessor: item => item.insurable.insuranceRequirement,
//     Cell: InsuranceRequirementsCell,
//     order: 100,
//     disableSortBy: true
//   }
// ];
//
// const PolicyItemsPopover = ({ companyId, insuredId, policy, disabled }) => {
//   const dispatch = useDispatch();
//   const { id: policyId } = policy;
//   const policyLinkedItemsData = usePolicyLinkedItemsData({ policyId, insuredId });
//   const { data: itemTypes, isLoading: itemTypesLoading } = useCompanyItemTypesData(companyId);
//   const { data: itemData, isLoading: linkedItemsLoading } = policyLinkedItemsData;
//
//   const defaultColumns = useMemo(() => getDefaultColumns(companyId, insuredId), []);
//
//   const { itemTypeProperties = [] } = useMemo(() => {
//     if (itemTypesLoading) {
//       return { itemTypeProperties: [] };
//     }
//
//     return itemTypes.find(type => type.name === 'unit') || {};
//   }, [itemTypes]);
//
//   const { isOpen, onOpen, onClose } = useDisclosure();
//
//   const hiddenColumns = [
//     'quote_number',
//     'vehicle_quote_status',
//     'approval_status',
//     'status',
//     'delv_dte'
//   ];
//
//  // I GOT STUCK HERE
//   const columns = useMemo(() => itemTypeProperties
//     .map(p => ({
//       Header: createHeaderCell(p.displayName),
//       // accessor: item => item.insurable.properties.find(itemProperty => itemProperty.item_type_property_id === p.id),
//       id: p.name,
//       accessor: item => item.insurable.properties,
//       cell: ({ value: { properties } = [], row }) => {
//         const { itemTypeProperty, value } = properties.find(itemProperty => itemProperty.item_type_property_id === p.id);
//
//         return (<PropertyCell dataType={itemTypeProperty.dataType} value={value} />);
//       },
//       order: p.order,
//       disableSortBy: true
//     }))
//     .concat(defaultColumns)
//     .sort((a, b) => a.order - b.order), [itemTypeProperties]);
//
//
//   if (disabled) {
//     return (
//       <GridItem colSpan={1}>
//         <IconButton
//           isDisabled
//           aria-label="disabled-item-policies-link-button"
//           icon={<Icon as={BsLink45Deg} h={5} w={5} />}
//           variant="textBlack"
//         />
//       </GridItem>
//     );
//   }
//
//   return (
//     <GridItem colSpan={1}>
//       <Popover
//         isLazy
//         isOpen={isOpen}
//         placement="bottom"
//         trigger="hover"
//         onClose={onClose}
//         onOpen={onOpen}
//       >
//         <PopoverTrigger>
//           <IconButton
//             aria-label="linked-policy-items-icon"
//             icon={<Icon as={BsLink45Deg} h={5} w={5} />}
//             variant="textPrimary"
//           />
//         </PopoverTrigger>
//         <PopoverContent>
//           <PopoverArrow />
//           <PopoverCloseButton />
//           <PopoverBody mt={3} mx={2}>
//             <Text mb={2} textAlign="left" textStyle="bodyXl">
//               {`Linked Items (${policyLinkedItemsData?.isLoading ? 'Loading...' : policyLinkedItemsData?.data?.pages[0].total})`}
//             </Text>
//             {linkedItemsLoading
//               ? (
//                 <GridItem colSpan={1} minW="200px">
//                   <LoadingSpinner isInline minHeight="200px" />
//                 </GridItem>
//               )
//               : (
//                 <ReactQueryInfiniteTable
//                   height="200px"
//                   reactQueryResponse={policyLinkedItemsData}
//                   tableColumns={columns}
//                 />
//               )}
//           </PopoverBody>
//         </PopoverContent>
//       </Popover>
//     </GridItem>
//   );
// };
//
// PolicyItemsPopover.propTypes = {
//   companyId: PropTypes.number.isRequired,
//   insuredId: PropTypes.number.isRequired,
//   policy: PropTypes.shape({ id: PropTypes.number }).isRequired
// };
//
// export default PolicyItemsPopover;
